import React, { useEffect, useState } from "react";
import {
  Row,
  Card,
  Button,
  Form,
  Col,
  FormGroup,
  FormLabel,
} from "react-bootstrap";
import { ErrorMessage, Field, Formik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getCategories } from "../../../../services/CategoryService";
import {
  createSkill,
  getSkillById,
  updateSkill,
} from "../../../../services/SkillService";
import AsyncSelect from "react-select/async";

const AddSkill = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [skillData, setSkillData] = useState({ name: "", category: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [categories, setCategories] = useState([]);

  const fetchData = async () => {
    try {
      const res = await getCategories(); // Fetching Category
      setCategories(res);
    } catch (error) {
      console.error("Error fetching Category:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (id) {
      fetchSkillDetails(id);
    } else {
      setSkillData({ name: "", category: "" });
      setIsEditing(false);
    }
  }, [id]);

  const fetchSkillDetails = async (skillId) => {
    try {
      const skills = await getSkillById(skillId);
      if (skills) {
        setSkillData(skills);
        setIsEditing(true);
      }
    } catch (error) {
      console.error("Error fetching skill details:", error);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    category: Yup.string().required(),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formattedValues = {
        name: values.name.toLowerCase(),
        category: values.category.toLowerCase(),
      };
      if (isEditing) {
        await updateSkill(id, formattedValues);
        toast.success("Skill updated successfully!");
      } else {
        await createSkill(formattedValues);
        toast.success("Skill added successfully!");
      }
      resetForm();
      navigate("/settings/skills"); // Redirect to skill list after submission
    } catch (error) {
      console.error("Error processing skill:", error);
      toast.error("Error processing skill!");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <Formik
        enableReinitialize
        initialValues={skillData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit} className="p-4">
            <h4>{isEditing ? "Edit Skill" : "Add Skill"}</h4>
            <Card className="p-4">
              <Row className="mb-3">
                <Col>
                  <div className="form-group">
                    <label htmlFor="name">Skill Name</label>
                    <Field
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Category"
                      value={values.name}
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </Col>

                <Col>
                  <FormGroup>
                    <FormLabel
                      className="col-lg-4 col-form-label"
                      htmlFor={"category"}
                    >
                      Select Category
                    </FormLabel>

                    <AsyncSelect
                      name="category"
                      defaultOptions={categories.map((category) => ({
                        label: category.name.toUpperCase(),
                        value: category.name.toLowerCase(),
                      }))}
                      placeholder="Select category"
                      isSearchable
                      onChange={(selected) =>
                        setFieldValue("category", selected.value)
                      }
                      value={
                        values.category
                          ? categories
                              .map((category) => ({
                                label: category.name.toUpperCase(),
                                value: category.name.toLowerCase(),
                              }))
                              .find((option) => option.value === values.category.toLowerCase()) || null
                          : null
                      }
                    />

                    <ErrorMessage
                      name={"category"}
                      component="div"
                      className="error-message text-danger"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={3} className="text-start">
                  <Link to="/skill">
                    <Button variant="danger" size="sm">
                      Cancel
                    </Button>
                  </Link>
                </Col>
                <Col className="text-end me-3">
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Processing..."
                      : isEditing
                      ? "Update"
                      : "Submit"}
                  </Button>
                </Col>
              </Row>
            </Card>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddSkill;
